import { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { useWorkspace } from './WorkspaceContext';
import { Send, MessageSquare } from 'lucide-react';

const AVATAR_COLORS = ['#dc2626', '#2563eb', '#16a34a', '#7c3aed', '#d97706', '#0891b2'];

function avatarColor(name) {
  return AVATAR_COLORS[(name || '?').charCodeAt(0) % AVATAR_COLORS.length];
}

function timeAgo(dateStr) {
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 172800) return 'yesterday';
  return `${Math.floor(diff / 86400)}d ago`;
}

function rolePillStyle(role) {
  if (role === 'Partner') return { background: 'rgba(220,38,38,0.12)', color: '#dc2626' };
  if (role === 'Associate') return { background: 'rgba(37,99,235,0.12)', color: '#2563eb' };
  return { background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.4)' };
}

export default function CommentBox({ entityType, entityId, entityName }) {
  const ctx = useWorkspace();
  const workspace = ctx?.workspace;
  const members = ctx?.members || [];
  const logActivity = ctx?.logActivity;

  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (workspace?.id && entityId) fetchComments();
  }, [workspace?.id, entityId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchComments = async () => {
    const { data } = await supabase
      .from('comments')
      .select('*')
      .eq('workspace_id', workspace.id)
      .eq('entity_type', entityType)
      .eq('entity_id', String(entityId))
      .order('created_at', { ascending: true });
    setComments(data || []);
  };

  const handleSubmit = async () => {
    if (!newComment.trim() || loading || !workspace?.id) return;
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;
      const currentMember = members.find(m => m.user_id === session.user.id);
      const { error } = await supabase.from('comments').insert({
        workspace_id: workspace.id,
        entity_type: entityType,
        entity_id: String(entityId),
        user_id: session.user.id,
        user_name: currentMember?.full_name || session.user.email,
        user_role: currentMember?.role || '',
        text: newComment.trim(),
      });
      if (!error) {
        setNewComment('');
        await fetchComments();
        logActivity?.('commented', entityType, entityId, entityName, {});
      }
    } finally {
      setLoading(false);
    }
  };

  if (!workspace) return null;

  return (
    <div style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <MessageSquare style={{ width: 13, height: 13, color: 'rgba(255,255,255,0.3)' }} />
        <p style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.1em', margin: 0 }}>
          Team Comments {comments.length > 0 && `(${comments.length})`}
        </p>
      </div>

      {comments.length > 0 && (
        <div style={{ maxHeight: 280, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 12 }}>
          {comments.map((c, i) => (
            <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <div style={{ width: 26, height: 26, borderRadius: '50%', background: avatarColor(c.user_name), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, color: '#fff', flexShrink: 0 }}>
                {(c.user_name || '?').charAt(0).toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#fff' }}>{c.user_name}</span>
                  {c.user_role && (
                    <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 100, ...rolePillStyle(c.user_role) }}>
                      {c.user_role}
                    </span>
                  )}
                  <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.25)', fontWeight: 500 }}>{timeAgo(c.created_at)}</span>
                </div>
                <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', margin: 0, lineHeight: 1.5, fontWeight: 400 }}>{c.text}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8 }}>
        <input
          type="text"
          value={newComment}
          onChange={e => setNewComment(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSubmit()}
          placeholder="Add a comment..."
          style={{ flex: 1, padding: '9px 12px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 9, outline: 'none', fontSize: 12, fontWeight: 500, color: '#fff', fontFamily: 'inherit' }}
          onFocus={e => e.target.style.borderColor = '#dc2626'}
          onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.08)'}
        />
        <button
          onClick={handleSubmit}
          disabled={loading || !newComment.trim()}
          style={{ width: 34, height: 34, background: '#dc2626', border: 'none', borderRadius: 9, cursor: loading || !newComment.trim() ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: loading || !newComment.trim() ? 0.5 : 1, flexShrink: 0 }}
        >
          <Send style={{ width: 13, height: 13, color: '#fff' }} />
        </button>
      </div>
    </div>
  );
}
